import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiDatabase, FiActivity, FiShield, FiUsers, FiArrowUpRight, FiTrendingUp, FiInfo, FiAnchor } from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';

const stats = [
  { label: 'Total Datasets', value: '1,248', change: '+12 this week', icon: FiDatabase, color: 'var(--schema-teal)' },
  { label: 'Harbor Health', value: '94.2%', change: '+0.5% this week', icon: FiShield, color: 'var(--success-mint)' },
  { label: 'Pending Access', value: '24', change: '-3 since yesterday', icon: FiUsers, color: 'var(--alert-coral)' },
  { label: 'Query Volume', value: '8.4M', change: '+1.2M this week', icon: FiActivity, color: 'var(--pearl)' },
];

export default function Overview() {
  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: [{ type: 'category', boundaryGap: false, data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], axisLine: { lineStyle: { color: 'var(--border-ocean)' } } }],
    yAxis: [{ type: 'value', splitLine: { lineStyle: { color: 'var(--border-ocean)', type: 'dashed' } } }],
    series: [{ name: 'Queries', type: 'line', smooth: true, lineStyle: { width: 3, color: 'var(--schema-teal)' }, areaStyle: { opacity: 0.2, color: 'var(--schema-teal)' }, data: [120, 132, 101, 134, 90, 230, 210] }]
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--pearl)] mb-1">Harbor Command Center</h2>
          <p className="text-[var(--pearl-muted)] text-sm">Real-time telemetry of your data ecosystem.</p>
        </div>
        <button className="btn-secondary flex items-center gap-2 self-start">
          <SafeIcon icon={FiTrendingUp} /> Generate Executive Sonar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }} className="glass-panel p-5 glass-panel-hover">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 rounded-lg bg-[var(--surface-ocean-light)] border border-[var(--border-ocean)]">
                <SafeIcon icon={stat.icon} style={{ color: stat.color }} className="text-xl" />
              </div>
              <span className="flex items-center text-[10px] text-[var(--schema-teal)] bg-[var(--schema-teal-dim)] px-2 py-0.5 rounded-full uppercase tracking-tighter">Live Monitor</span>
            </div>
            <h3 className="text-3xl font-bold text-[var(--pearl)] mb-1">{stat.value}</h3>
            <p className="text-sm text-[var(--pearl-muted)] font-medium mb-1">{stat.label}</p>
            <p className="text-xs text-[var(--pearl-muted)] opacity-70">{stat.change}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="glass-panel p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-[var(--schema-teal-dim)]">
                <SafeIcon icon={FiInfo} className="text-[var(--schema-teal)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--pearl)]">What is DataHarbor?</h3>
            </div>
            <div className="text-[var(--pearl-muted)] text-sm leading-relaxed space-y-3">
              <p>DataHarbor is an <span className="text-[var(--schema-teal)] font-medium">Enterprise Data Catalog</span> designed for modern data teams. We solve the chaos of fragmented internal datasets by providing a unified, searchable portal that acts as the "Google for your company's data."</p>
              <p>By indexing metadata across your entire stack—from Snowflake to Kafka—we ensure that every analyst can find, understand, and safely access the data they need, while governance teams maintain total control through automated quality beacons and lineage tracking.</p>
            </div>
          </div>

          <div className="glass-panel p-6">
            <h3 className="text-lg font-semibold text-[var(--pearl)] mb-4 flex items-center gap-2">
              <SafeIcon icon={FiActivity} className="text-[var(--schema-teal)]" /> Harbor Traffic
            </h3>
            <div className="h-[250px] w-full">
              <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
            </div>
          </div>
        </div>

        <div className="glass-panel p-6 flex flex-col">
          <h3 className="text-lg font-semibold text-[var(--pearl)] mb-4">Recent Dockings</h3>
          <div className="space-y-4 flex-1 overflow-auto max-h-[500px] pr-2">
            {[1, 2, 3, 4, 5].map((_, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[var(--surface-ocean-light)]/50 border border-[var(--border-ocean)]">
                <div className="w-2 h-2 rounded-full bg-[var(--schema-teal)] mt-2 shadow-[0_0_5px_var(--schema-teal)]"></div>
                <div>
                  <p className="text-sm font-medium text-[var(--pearl)]">Metadata Sync Success</p>
                  <p className="text-xs text-[var(--schema-teal)] mt-0.5 font-mono">sector-7-node-{i+100}</p>
                  <p className="text-[10px] text-[var(--pearl-muted)] mt-1 italic">{i * 15 + 5} mins ago</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}